import pkceChallenge from 'pkce-challenge';
import { isStrEmpty } from '@eforms/forms-common-ui';
import { LWB_ASSERTION_API_PATH } from "../constants/ApiEndPoint";
import request from './request';

const URL_TOKEN_PARAMETER_NAME = "mtoken";
export const SESSION_STORAGE_KEY_ACCESS_TOKEN = "accessToken";
export const SESSION_STORAGE_KEY_ID_TOKEN = "idToken";
export const SESSION_STORAGE_KEY_CHANNEL = "channel";
export const LOGIN_WITH_BANK = "login with bank"

/**
 * Attempts to extract an access token from the URL and returns it if found; otherwise returns null
 */
export const extractTokenFromURL = () => {
  const urlObject = new URL(window.location.href);
  const searchParams = new URLSearchParams(urlObject.search);
  return searchParams.get(URL_TOKEN_PARAMETER_NAME);
};

/**
 * Returns the access token from session storage if available
 */
export const getAccessToken = () =>
  sessionStorage.getItem(SESSION_STORAGE_KEY_ACCESS_TOKEN);

/**
 * Saves the access token in session storage
 */
export const saveAccessToken = (token) =>
  sessionStorage.setItem(SESSION_STORAGE_KEY_ACCESS_TOKEN, token);

/**
 * Clear the value in session storage by its key
*/
export const removeSessionItem = (key) =>
  sessionStorage.removeItem(key);

/**
 * Returns 10 digit random number as string
 */

export const generateRandomId = (length = 10) => {
  return Array.from({ length }, () => Math.floor(Math.random() * 10)).join("");
};

/**
 * Fetches the IAM access token
 */
export const fetchIAMAccessToken = async (token, codeVerifier) => {
  const channel = sessionStorage.getItem(SESSION_STORAGE_KEY_CHANNEL);

  const requestData = {
    client_id: channel === LOGIN_WITH_BANK ? window.environmentConfig.lwb.clientId : window.environmentConfig.iam.client_id,
    nonce: generateRandomId(),
    token: token,
    tokenType: channel === LOGIN_WITH_BANK ? "CODE" : "JWT",
    redirect_uri: "urn:ietf:wg:oauth:2.0:auto:oo",
    code_verifier: codeVerifier,
    scope: channel === LOGIN_WITH_BANK ? window.environmentConfig.lwb.scope : window.environmentConfig.iam.scope
  };

  const res = await fetch(`${window.environmentConfig.iam.apiBaseUri}/issue`, {
    method: "POST",
    mode: "cors",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(requestData),
  });
  const { access_token } = await res.json();
  return access_token;
};

/**
 * Renews the IAM access token
 */
export const renewIAMToken = async () => {
  const channel = sessionStorage.getItem(SESSION_STORAGE_KEY_CHANNEL);
  const token = getAccessToken();
  const res = await fetch(`${window.environmentConfig.iam.apiBaseUri}/renew`, {
    method: "PATCH",
    mode: "cors",
    credentials: "include",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      access_token: token,
      client_id: channel === LOGIN_WITH_BANK ? window.environmentConfig.lwb.clientId : window.environmentConfig.iam.client_id,
    }),
  });
  const { access_token } = await res.json();
  return access_token;
};

/**
 * Fetches the assersion to get the IAM auth code
 * @param formUuid form uuid which is extracted from the url and set in the request header
 * @return object which contains either error or the auth code and code verifier
*/
export const getLWBAccessToken = async (formUuid) => {
  const idToken = sessionStorage.getItem(SESSION_STORAGE_KEY_ID_TOKEN);
  removeSessionItem(SESSION_STORAGE_KEY_ID_TOKEN);
 
  if (!isStrEmpty(idToken)) {
    sessionStorage.setItem(SESSION_STORAGE_KEY_CHANNEL, LOGIN_WITH_BANK);
    return await request
      .post(`${LWB_ASSERTION_API_PATH}`)
      .set('Content-Type', 'application/json')
      .set('x-form-uuid', formUuid)
      .withCredentials()
      .send(JSON.stringify({ idToken: idToken })).then((response) => {
        return getAuthCode(response.body.assertion);
      }).catch((error) => {
        console.error("🚀 ~ file: authUtils.js:105 ~ .send ~ error:", error)
        return { error }
      });
  }
}

/**
 * Fetches the Auth code from IAM endpoint
 * @param {*} assertion 
 * @returns object which contains either error or the auth code and code verifier
 */
const getAuthCode = async (assertion) => {
  const { code_challenge, code_verifier } = pkceChallenge();

  let requestData = new URLSearchParams({
    response_mode: 'form_post',
    assertion: assertion,
    scope: window.environmentConfig.lwb.authCodeScope,
    nonce: generateRandomId(),
    acr_values: window.environmentConfig.lwb.acrValue,
    client_id: window.environmentConfig.lwb.clientId,
    response_type: 'code',
    code_challenge: code_challenge,
    code_challenge_method: 'S256',
    redirect_uri: "urn:ietf:wg:oauth:2.0:auto:oo"
  });

  return await fetch(`${window.environmentConfig.oAuth2uri}?${requestData.toString()}`, {
    method: "GET",
    mode: "cors",
    credentials: "include",
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'accept': 'application/x-www-form-urlencoded'
    }
  }).then(async (response) => {
      const data = await decodeResponseStream(response);
      let domParser = new DOMParser();
      let doc = domParser.parseFromString(data, 'text/html');

      let input = doc.querySelector('input[name="code"]');

      if(input) {
         return { code: input.value, codeVerifier: code_verifier }
      } else {
        return { error: doc.querySelector('input[name="error"]').value  }
      }

    }).catch((error) => {
      console.error("🚀 ~ file: authUtils.js:144 ~ .send ~ error:", error)
      return { error }
    });
}

const decodeResponseStream = async (response) => {
  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let result = '';
  let done = false;

  while (!done) {
    const { value, done: streamDone } = await reader.read();
    done = streamDone;
    if (value) {
      result += decoder.decode(value, { stream: !done });
    }
  }
  return result;
}